import { InjectionToken } from '@angular/core';
import { LocalCache } from '@nk/caching-lib';
import {
  AppFeatureKey,
  CompanySensitiveDataCompositeEntity,
  SensitiveDataCompanyAccessBase,
  SensitiveDataCompanyAccessEntity,
  SensitiveDataParams,
  SensitiveDataPersonAccessEntity,
  SensitiveDataTypeEntity,
  SensitiveDataTypeKey,
  SensitiveDataWatchlistAccessBase,
  SensitiveDataWatchlistAccessEntity,
  WatchlistSensitiveDataCompositeEntity,
} from '@nk/data-entities-lib';
import { Observable } from 'rxjs';

export interface SensitiveDataApiService {
  loadTriggered$: Observable<unknown>;

  triggerSensitiveDataLoad(): void;

  hasFeatureAndRoleAccess(appFeatureKey?: AppFeatureKey): Observable<boolean>;

  sensitiveDataWatchlistAccessEntityListCache: LocalCache<
    SensitiveDataApiService,
    () => Observable<SensitiveDataWatchlistAccessEntity[]>
  >;

  sensitiveDataCompanyAccessEntityListCache: LocalCache<
    SensitiveDataApiService,
    () => Observable<SensitiveDataCompanyAccessEntity[]>
  >;

  sensitiveDataTypeListCache: LocalCache<SensitiveDataApiService, () => Observable<SensitiveDataTypeEntity[]>>;

  sensitiveDataCompositeByParamsCache: LocalCache<
    SensitiveDataApiService,
    (
      appCustomerUuid: string,
      params: SensitiveDataParams,
    ) => Observable<CompanySensitiveDataCompositeEntity[] | WatchlistSensitiveDataCompositeEntity[]>
  >;

  companySensitiveDataCompositeListCache: LocalCache<
    SensitiveDataApiService,
    (appCustomerUuid: string, companyUuid?: string) => Observable<CompanySensitiveDataCompositeEntity[]>
  >;

  watchlistSensitiveDataCompositeListCache: LocalCache<
    SensitiveDataApiService,
    (appCustomerUuid: string, appWatchlistUuid?: string) => Observable<WatchlistSensitiveDataCompositeEntity[]>
  >;

  sensitiveDataPersonAccessCache: LocalCache<
    SensitiveDataApiService,
    (
      personUuid: string,
      appMembershipUuid: string,
      appCustomerUuid: string,
    ) => Observable<SensitiveDataPersonAccessEntity[]>
  >;

  postSensitiveDataWatchlistAccessEntity(
    payload: SensitiveDataWatchlistAccessBase,
  ): Observable<SensitiveDataWatchlistAccessEntity>;

  postSensitiveDataCompanyAccessEntity(
    payload: SensitiveDataCompanyAccessBase,
  ): Observable<SensitiveDataCompanyAccessEntity>;

  deleteSensitiveDataWatchlistAccessEntity(
    sensitiveDataWatchlistAccessUuid: string,
    appCustomerUuid: string,
  ): Observable<SensitiveDataWatchlistAccessEntity>;

  deleteSensitiveDataCompanyAccessEntity(
    sensitiveDataCompanyAccessUuid: string,
    appCustomerUuid: string,
  ): Observable<SensitiveDataCompanyAccessEntity>;

  postSensitiveDataPersonAccess(
    personUuid: string,
    appMembershipUuid: string,
    appCustomerUuid: string,
    sensitiveDataTypeUuid: string,
  ): Observable<SensitiveDataPersonAccessEntity[]>;

  fetchSensitiveDataTypesByWatchlist(watchlistUuid: string): Observable<SensitiveDataTypeEntity[]>;

  fetchSensitiveDataTypesByParams(params: SensitiveDataParams): Observable<SensitiveDataTypeEntity[]>;

  fetchSensitiveDataTypesByCompany(companyIdentifier: string): Observable<SensitiveDataTypeEntity[]>;

  claimValidSensitiveDataTypeKeysByParams(
    params: SensitiveDataParams,
    sensitiveDataTypeKeys: SensitiveDataTypeKey[],
  ): Observable<SensitiveDataTypeKey[]>;

  claimValidSensitiveDataTypeKeysByWatchlist(
    watchlistUuid: string,
    sensitiveDataTypeKeys: SensitiveDataTypeKey[],
  ): Observable<SensitiveDataTypeKey[]>;

  claimValidSensitiveDataTypeKeysByCompany(
    companyIdentifier: string,
    sensitiveDataTypeKeys: SensitiveDataTypeKey[],
  ): Observable<SensitiveDataTypeKey[]>;
}

export const SENSITIVE_DATA_API_SERVICE_TOKEN = new InjectionToken<SensitiveDataApiService>('SensitiveDataApiService');
